// @flow
import { Box } from 'grommet';
import * as React from 'react';
import { Route, withRouter } from 'react-router-dom';
import { useLeaflet, withLeaflet } from 'react-leaflet';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import { FormattedMessage, injectIntl } from 'react-intl';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import PipeIcon from 'esm-web-pro/components/layout/sidePanel/icons/PipeIcon';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import ModelAttributes from 'esm-web-pro/containers/pages/network/ModelAttributes';
import AssetAttributes from './AssetAttributes';
import AssetActions from './AssetActions';


type Props = {
    panelId: String,
    object: Object,
    map: { is_draft: boolean, uuid: string },
    history: Object,
    match: Object,
    updateObject: Function,
    editor: BaseEditor,
}

const Asset = (props: Props) => {
    const {
        object: asset, map: networkMap, updateObject,
        editor, match, panelId,
    } = props;
    const leaflet = useLeaflet();

    const numberPointCallback = React.useCallback(
        UseHighlightPoint(
            asset.container.coordinates.coordinates,
            asset.container.alias || asset.container.sequence,
        ), [asset],
    );

    const handleView = React.useCallback(() => {
        editor.unregisterAllLoadingCallback();
        editor.registerLoadingCallback(numberPointCallback);
        if (leaflet.map) {
            const coords = asset.container.coordinates.coordinates;
            leaflet.map.highlightedGeometry = {
                bounds: null, center: [coords[1], coords[0]],
            };
            leaflet.map.panTo([coords[1], coords[0]]);
        }
    }, [editor, leaflet, numberPointCallback, asset]);

    React.useEffect(() => {
        handleView();

        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [handleView, editor]);

    const navigateBack = () => {
        props.history.push(props.match.url.split('/layer')[0]);
    };

    const handleEdit = (data: Object) => {
        updateObject(data);
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={asset.ref}
            titleLabel={<FormattedMessage id="asset-panel-title" defaultMessage="Asset" />}
            titleIcon={<PipeIcon />}
            actions={<NavigateBackButton panelId={panelId} />}
        >
            <Box height="100%" justify="between">
                <Box flex>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <AssetAttributes
                            asset={asset}
                            url={match.url}
                            onView={handleView}
                        />
                        <HorizontalLine color="grey" />
                        <Box direction="row">
                            <AssetActions
                                asset={asset}
                                map={networkMap}
                                onEdit={handleEdit}
                                navigateBack={navigateBack}
                                editor={editor}
                            />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                </Box>
            </Box>
            <Route
                path={`${match.path}/model/:modelCode`.replace('//', '/')}
                render={({ match: _match }) => (
                    <ModelAttributes url={_match.url} />
                )}
            />
        </SidePane>
    );
};

export default withRouter(withLeaflet(injectIntl(Asset)));
